import cn from 'classnames';
import { useEffect, useState } from 'react';

import { usePdfHelpers } from 'src/hooks/usePdfHelpers';

import { setJumpLinkAttributes } from 'src/utils/jumpLinks';

import { JumpLinkLabel, LinkData } from 'src/types/components';

import ArrowForward from 'assets/icons/arrow-forward.svg';

import Cta from 'components/BaseHelpers/Cta';
import Link from 'components/BaseHelpers/Link';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import styles from './LinksGridSection.module.scss';

export type LinksGridSectionProps = {
  heading: string;
  description: string;
  links: (LinkData & { excerpt?: string })[];
  displayCount: number;
  showAllLabel: string;
  showAll?: boolean;
} & JumpLinkLabel;

const LinksGridSection = (props: LinksGridSectionProps): JSX.Element => {
  const { heading, description, links, displayCount, showAllLabel, jumpLinkLabel, showAll } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const { isPdf } = usePdfHelpers();

  useEffect(() => {
    setIsExpanded(false);
  }, [links, displayCount]);

  if (links.length === 0) {
    return <></>;
  }

  const itemsToDisplay = showAll || isPdf || isExpanded ? links : links.slice(0, displayCount);

  const List = ({ items, className }: { items: typeof links; className?: string }) => (
    <ul className={cn(styles.list, className)}>
      {items.map((link, index) => (
        <li key={`${link.href}-${index}`} className={styles['list-item']}>
          <Link
            className={cn(styles.link, 'heading-h4')}
            href={link.href}
            openNewTab={link.target === '_blank'}
          >
            <Text className={styles.label} field={link.label} />
            <ArrowForward
              className={cn(styles.icon, {
                [styles['icon--new-tab']]: link.target === '_blank',
              })}
            />
          </Link>
          <RichText
            field={link.excerpt}
            className={cn(styles.excerpt, 'body-sm')}
            noDefaultBodyStyle={true}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <div className={cn('container-10')} {...setJumpLinkAttributes(jumpLinkLabel)}>
      <Text tag="h2" className={cn('heading-h3', styles.heading)} field={heading} />
      <RichText className="body-med" field={description} />
      <List className={styles['top-list']} items={itemsToDisplay} />
      {itemsToDisplay.length < links.length && (
        <Cta tag="button" onClick={() => setIsExpanded(true)} className={styles.cta}>
          {showAllLabel}
        </Cta>
      )}
    </div>
  );
};

export default LinksGridSection;
